import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { CreateThreadPayload, Thread } from '../models/Thread';
import { ThreadContentFormat, ThreadSubjectType } from '../models/Thread';
import { UserContext } from '@/shared/contexts/UserContext';
import { useApi } from '../services/api';
import { useSnackbar } from '../services/SnackbarService';

interface UseThreadsOptions {
  subjectType: ThreadSubjectType;
  subjectId: number;
  isAuthenticated?: boolean;
  autoFetch?: boolean;
  /** Label used in snackbar messages (e.g. "Note" or "Thread"). Defaults to "Thread". */
  entityLabel?: string;
}

interface UseThreadsReturn {
  threads: Thread[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  createThread: (payload: CreateThreadPayload) => Promise<Thread | null>;
  createReply: (parentId: number, content: string, isSlateFormat?: boolean) => Promise<Thread | null>;
  updateThread: (threadId: number, content: string, contentFormat: ThreadContentFormat) => Promise<Thread | null>;
  deleteThread: (threadId: number) => Promise<boolean>;
  creating: boolean;
}

const addReplyToThread = (threads: Thread[], parentId: number, newReply: Thread): Thread[] => {
  return threads.map((thread) => {
    if (thread.id === parentId) {
      const updated = {
        ...thread,
        children: [...(thread.children || []), newReply],
      };
      return updated;
    }
    if (thread.children && thread.children.length > 0) {
      return {
        ...thread,
        children: addReplyToThread(thread.children, parentId, newReply),
      };
    }
    return thread;
  });
};

const updateThreadInTree = (threads: Thread[], threadId: number, updates: Partial<Thread>): Thread[] => {
  return threads.map((thread) => {
    if (thread.id === threadId) {
      return { ...thread, ...updates };
    }
    if (thread.children && thread.children.length > 0) {
      return {
        ...thread,
        children: updateThreadInTree(thread.children, threadId, updates),
      };
    }
    return thread;
  });
};

const removeThreadFromTree = (threads: Thread[], threadId: number): Thread[] => {
  return threads
    .filter((thread) => thread.id !== threadId)
    .map((thread) => {
      if (thread.children && thread.children.length > 0) {
        return {
          ...thread,
          children: removeThreadFromTree(thread.children, threadId),
        };
      }
      return thread;
    });
};

export const useThreads = ({
  subjectType,
  subjectId,
  isAuthenticated = false,
  autoFetch = true,
  entityLabel = 'Thread',
}: UseThreadsOptions): UseThreadsReturn => {
  const api = useApi();
  const { user } = useContext(UserContext);
  const { showSnackbar } = useSnackbar();
  const [threads, setThreads] = useState<Thread[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasFetchedRef = useRef(false);

  const fetchThreads = useCallback(
    async (showLoading = true) => {
      if (!subjectId || !isAuthenticated) return;

      if (showLoading) {
        setLoading(true);
      }
      setError(null);

      try {
        const response = await api.get<{ data: Thread[] }>(`/thread/subject/${subjectType}/${subjectId}`);

        if (Array.isArray(response)) {
          setThreads(response);
        } else if (response?.data && Array.isArray(response.data)) {
          setThreads(response.data);
        } else {
          setThreads([]);
        }
      } catch (err: any) {
        setError('Failed to load threads');
        setThreads([]);
      } finally {
        setLoading(false);
      }
    },
    [api, subjectType, subjectId, isAuthenticated],
  );

  const createThread = useCallback(
    async (payload: CreateThreadPayload): Promise<Thread | null> => {
      if (!isAuthenticated || !user) return null;

      setCreating(true);
      setError(null);

      const optimisticThread: Thread = {
        id: Date.now(),
        subjectType: payload.subjectType,
        subjectId: payload.subjectId,
        parentId: payload.parentId || null,
        userId: user.id!,
        content: payload.content,
        contentFormat: payload.contentFormat || ThreadContentFormat.SlateJson,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        user: {
          id: user.id!,
          firstName: user.firstName,
          lastName: user.lastName,
          username: user.username,
          avatarColor: user.avatarColor,
        },
        children: [],
      };

      const isReply = !!payload.parentId;

      if (isReply && payload.parentId) {
        setThreads((prev) => {
          const updated = addReplyToThread(prev, payload.parentId!, optimisticThread);
          return updated;
        });
      } else {
        setThreads((prev) => [optimisticThread, ...prev]);
      }

      try {
        const response = await api.post<{ data: Thread }>('/thread', payload);

        if (response?.data) {
          const realThread = response.data;

          if (isReply && payload.parentId) {
            setThreads((prev) => {
              const updateReply = (threads: Thread[]): Thread[] =>
                threads.map((t) => {
                  if (t.id === payload.parentId) {
                    return {
                      ...t,
                      children:
                        t.children?.map((c) => {
                          if (c.id === optimisticThread.id) {
                            return { ...realThread, children: [] };
                          }
                          return c;
                        }) || [],
                    };
                  }
                  if (t.children && t.children.length > 0) {
                    return { ...t, children: updateReply(t.children) };
                  }
                  return t;
                });
              const updated = updateReply(prev);
              return updated;
            });
          } else {
            setThreads((prev) => prev.map((t) => (t.id === optimisticThread.id ? { ...realThread, children: [] } : t)));
          }
          showSnackbar(isReply ? 'Reply posted successfully' : `${entityLabel} posted successfully`, 'success');
          return response.data;
        }

        if (isReply && payload.parentId) {
          setThreads((prev) => {
            const revert = (threads: Thread[]): Thread[] =>
              threads.map((t) => {
                if (t.id === payload.parentId) {
                  return {
                    ...t,
                    children: t.children?.filter((c) => c.id !== optimisticThread.id) || [],
                  };
                }
                if (t.children) {
                  return { ...t, children: revert(t.children) };
                }
                return t;
              });
            return revert(prev);
          });
        } else {
          setThreads((prev) => prev.filter((t) => t.id !== optimisticThread.id));
        }
        return null;
      } catch (err: any) {
        if (isReply && payload.parentId) {
          setThreads((prev) => {
            const revert = (threads: Thread[]): Thread[] =>
              threads.map((t) => {
                if (t.id === payload.parentId) {
                  return {
                    ...t,
                    children: t.children?.filter((c) => c.id !== optimisticThread.id) || [],
                  };
                }
                if (t.children) {
                  return { ...t, children: revert(t.children) };
                }
                return t;
              });
            return revert(prev);
          });
        } else {
          setThreads((prev) => prev.filter((t) => t.id !== optimisticThread.id));
        }
        setError(`Failed to post ${entityLabel.toLowerCase()}`);
        showSnackbar(`Failed to post ${entityLabel.toLowerCase()}. Please try again.`, 'error');
        return null;
      } finally {
        setCreating(false);
      }
    },
    [api, isAuthenticated, user, fetchThreads, showSnackbar],
  );

  const createReply = useCallback(
    async (parentId: number, content: string, isSlateFormat: boolean = false): Promise<Thread | null> => {
      return createThread({
        content,
        contentFormat: isSlateFormat ? ThreadContentFormat.SlateJson : ThreadContentFormat.PlainText,
        parentId,
        subjectId,
        subjectType,
      });
    },
    [createThread, subjectId, subjectType],
  );

  const updateThread = useCallback(
    async (threadId: number, content: string, contentFormat: ThreadContentFormat): Promise<Thread | null> => {
      if (!isAuthenticated) return null;

      setThreads((prev) =>
        updateThreadInTree(prev, threadId, {
          content,
          contentFormat,
          updatedAt: new Date().toISOString(),
        }),
      );

      try {
        const response = await api.put<{ data: Thread }>(`/thread/${threadId}`, {
          content,
          contentFormat,
        });

        if ((response as any)?.error) {
          await fetchThreads(false);
          const errorMessage = (response as any)?.message || 'Failed to update comment';
          setError(errorMessage);
          showSnackbar(errorMessage, 'error');
          return null;
        }

        if (response?.data) {
          setThreads((prev) =>
            updateThreadInTree(prev, threadId, {
              content: response.data.content,
              contentFormat: response.data.contentFormat,
              updatedAt: response.data.updatedAt,
            }),
          );
          showSnackbar('Comment updated successfully', 'success');
          return response.data;
        }

        await fetchThreads(false);
        return null;
      } catch (err: any) {
        fetchThreads(false);
        const errorMessage = err?.response?.data?.error || 'Failed to update comment';
        setError(errorMessage);
        showSnackbar(errorMessage, 'error');
        return null;
      }
    },
    [api, isAuthenticated, fetchThreads, showSnackbar],
  );

  const deleteThread = useCallback(
    async (threadId: number): Promise<boolean> => {
      if (!isAuthenticated) return false;

      setThreads((prev) =>
        updateThreadInTree(prev, threadId, {
          content: '[Comment deleted]',
          contentFormat: ThreadContentFormat.PlainText,
          deletedAt: new Date().toISOString(),
        }),
      );

      try {
        await api.delete(`/thread/${threadId}`);
        showSnackbar('Comment deleted', 'success');
        return true;
      } catch (err: any) {
        fetchThreads(false);
        const errorMessage = err?.response?.data?.error || 'Failed to delete comment';
        setError(errorMessage);
        showSnackbar(errorMessage, 'error');
        return false;
      }
    },
    [api, isAuthenticated, threads, fetchThreads, showSnackbar],
  );

  useEffect(() => {
    if (autoFetch && subjectId && isAuthenticated && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchThreads();
    }
  }, [autoFetch, subjectId, isAuthenticated, fetchThreads]);

  useEffect(() => {
    hasFetchedRef.current = false;
  }, [subjectId]);

  return {
    creating,
    createReply,
    createThread,
    deleteThread,
    error,
    loading,
    refetch: fetchThreads,
    threads,
    updateThread,
  };
};
